import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetMonthlyStatusBreakdown } from '../redux/sagas/requests/project';
import { statusColor } from '../utils/statusColors';

const STATUS_ORDER = [
    'Awaiting Authorization',
    'Pending',
    'In Progress',
    'Testing',
    'Ready for Review',
    'Approved',
    'Completed',
    'On Hold',
    'Cancelled',
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ChartArea = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 200,
});

const Bars = styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    flex: 1,
    paddingBottom: 4,
    gap: 8,
});

const MonthColumn = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    height: '100%',
    minWidth: 0,
});

const StackContainer = styled(Box)({
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    position: 'relative',
});

const Stack = styled(Box, { shouldForwardProp: (prop) => prop !== '$height' })(({ $height }) => ({
    display: 'flex',
    flexDirection: 'column-reverse',
    width: '100%',
    height: `${$height}%`,
    borderRadius: 3,
    overflow: 'hidden',
}));

const Segment = styled(Box, { shouldForwardProp: (prop) => prop !== 'color' && prop !== '$height' })(({ color, $height }) => ({
    backgroundColor: color,
    width: '100%',
    height: `${$height}%`,
    transition: 'opacity 120ms',
    '&:hover': { opacity: 0.85 },
}));

const TotalLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    marginBottom: 2,
}));

const MonthLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    marginTop: 4,
}));

const Legend = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const LegendItem = styled(Box, { shouldForwardProp: (prop) => prop !== '$active' })(({ theme, $active }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.typography.size.xs,
    cursor: 'pointer',
    userSelect: 'none',
    opacity: $active ? 1 : 0.5,
}));

const LegendSwatch = styled(Box, { shouldForwardProp: (prop) => prop !== 'color' && prop !== 'active' })(({ color, active }) => ({
    width: 10,
    height: 10,
    borderRadius: 2,
    backgroundColor: active ? color : 'transparent',
    border: `1px solid ${color}`,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 150,
    color: theme.palette.text.secondary,
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({theme}) => ({
    '& .MuiToggleButton-root': {
        padding: theme.spacing(0.5, 1),
        fontSize: theme.typography.size.sm,
        textTransform: 'none',
    },
}));

const MonthlyStatusBreakdownWidget = ({ widgetId, config = {}, onRemove }) => {
    const theme = useTheme();
    const [months, setMonths] = useState(6);

    const { data, isLoading, error, refresh } = useFetchQueue(widgetId, {
        apiCall: () => requestGetMonthlyStatusBreakdown(months),
        cacheKey: `monthly-status-breakdown-${months}`,
        transform: (response) => response || [],
    });

    // useFetchQueue caches apiCall in a ref and only fetches once; trigger
    // a refresh whenever the range changes.
    const isInitialRender = useRef(true);
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        refresh();
    }, [months, refresh]);

    const [hiddenStatuses, setHiddenStatuses] = useState(new Set());

    const { series, max, totalsByStatus } = useMemo(() => {
        const s = data || [];
        const totals = {};
        let maxTotal = 0;
        s.forEach((bucket) => {
            let total = 0;
            STATUS_ORDER.forEach((st) => {
                const c = bucket.statuses?.[st] || 0;
                if (!hiddenStatuses.has(st)) total += c;
                totals[st] = (totals[st] || 0) + c;
            });
            if (total > maxTotal) maxTotal = total;
        });
        return { series: s, max: maxTotal, totalsByStatus: totals };
    }, [data, hiddenStatuses]);

    const toggleStatus = (status) => {
        setHiddenStatuses((prev) => {
            const next = new Set(prev);
            if (next.has(status)) next.delete(status);
            else next.add(status);
            return next;
        });
    };

    const handleMonthsChange = (_e, next) => {
        if (next) setMonths(next);
    };

    const monthSelector = (
        <ToggleButtonGroup
            size="small"
            value={months}
            exclusive
            onChange={handleMonthsChange}
            aria-label="month range"
            className="widget-no-drag"
        >
            <ToggleButton value={3}>3M</ToggleButton>
            <ToggleButton value={6}>6M</ToggleButton>
            <ToggleButton value={12}>12M</ToggleButton>
        </ToggleButtonGroup>
    );

    const hasData = series.length > 0 && series.some((b) => Object.keys(b.statuses || {}).length > 0);

    return (
        <BaseWidget
            title="Monthly Status Breakdown"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
            actions={monthSelector}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">No project data in range</Typography>
                </EmptyState>
            ) : (
                <ChartArea>
                    <Bars>
                        {series.map((bucket) => {
                            const visibleTotal = STATUS_ORDER.reduce((sum, st) => (
                                hiddenStatuses.has(st) ? sum : sum + (bucket.statuses?.[st] || 0)
                            ), 0);
                            const heightPct = max > 0 ? (visibleTotal / max) * 100 : 0;
                            return (
                                <MonthColumn key={bucket.key}>
                                    <TotalLabel>{visibleTotal || ''}</TotalLabel>
                                    <StackContainer>
                                        <Stack $height={heightPct}>
                                            {STATUS_ORDER.map((st) => {
                                                if (hiddenStatuses.has(st)) return null;
                                                const count = bucket.statuses?.[st] || 0;
                                                if (!count) return null;
                                                const segPct = visibleTotal > 0 ? (count / visibleTotal) * 100 : 0;
                                                return (
                                                    <Tooltip key={st} title={`${st}: ${count}`} placement="top">
                                                        <Segment
                                                            color={statusColor(theme, 'project', st)}
                                                            $height={segPct}
                                                        />
                                                    </Tooltip>
                                                );
                                            })}
                                        </Stack>
                                    </StackContainer>
                                    <MonthLabel>
                                        {MONTH_LABELS[(bucket.month || 1) - 1]}
                                    </MonthLabel>
                                </MonthColumn>
                            );
                        })}
                    </Bars>
                    <Legend>
                        {STATUS_ORDER.filter((st) => totalsByStatus[st]).map((st) => {
                            const active = !hiddenStatuses.has(st);
                            return (
                                <LegendItem
                                    key={st}
                                    onClick={() => toggleStatus(st)}
                                    className="widget-no-drag"
                                    $active={active}
                                >
                                    <LegendSwatch color={statusColor(theme, 'project', st)} active={active} />
                                    {st}
                                </LegendItem>
                            );
                        })}
                    </Legend>
                </ChartArea>
            )}
        </BaseWidget>
    );
};

export default MonthlyStatusBreakdownWidget;
